import React, { memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actionCreators } from "./redux/productSalesSagas";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import SectionToggle from "../../../components/sectionToggle/sectionToggle";
import TableOrStackedBarChart from "./smartComponents/tableOrStackedBarChart";
import SecondaryCategory from "./smartComponents/secondaryCategory";
import CustomDateRanges from "./smartComponents/customDateRanges";
import PrimaryCategory from "./smartComponents/primaryCategories";
import IncludeReturns from "./smartComponents/includeReturns";
import GroupCategory from "./smartComponents/groupCategory";
import DatePicker from "./smartComponents/datePicker";
import PeriodType from "./smartComponents/periodType";
import ViewFilter from "../ViewFilter";
import KBLearnMore from "../../../components/learnMore/KBLearnMore";
import { ILEARN_URLS } from "../../../constants/ilearnUrls";
import "./productSalesModule.css";

const ProductSalesModule = (props) => {
  const dispatch = useDispatch();
  const useSecondaryCategories = useSelector(
    (state) => state.commonReducer.commonProps.useSecondaryCategories
  );
  const {
    isLoading,
    initialRequest,
    updatesLoading,
    filtersDirty,
    startDate,
    endDate,
    primaryCategoriesLoading,
    secondaryCategoriesLoading
  } = useSelector((state) => state.productSales);
  const includeReturnsLabel = useSelector(
    (state) => state.productSales.filters.toggles.includeReturns.label
  );
  const selectedRevenueView = useSelector((state) => state.productSales.selectedRevenueView);
  const isRevenueCategoryView = selectedRevenueView.id === "revenue_category";

  useEffect(() => {
    !initialRequest && dispatch(actionCreators.fetchCurrentUser(props));
  }, []);

  const renderUpdateBtn = () => {
    if (filtersDirty & !isLoading & (startDate !== "Invalid date") & (endDate !== "Invalid date")) {
      return (
        <button
          className="submit override-submit"
          onClick={() => dispatch(actionCreators.updateData())}
        >
          Update
        </button>
      );
    }

    return null;
  };

  const renderResetBtn = () => {
    if (filtersDirty & !isLoading) {
      return (
        <button
          className="submit reset override-submit"
          onClick={() => dispatch(actionCreators.resetFilters())}
        >
          Reset
        </button>
      );
    }

    return null;
  };

  return (
    <div id="product-sales-root">
      <SectionToggle
        autoHeight
        height={690}
        title="Product Sales by"
        customClass="product-sales-section"
        isLoading={isLoading}
        learnMore={
          <>
            <ViewFilter
              stateKey="productSales"
              updateRevenueView={actionCreators.updateRevenueView}
            />
            <KBLearnMore
              title="Product Sales"
              description="This report shows product sales across your selected date range, grouped by product category so you can compare performance side by side. These insights help you spot top sellers, evaluate inventory needs, and understand buying behavior within retail channels."
              kbUrl={ILEARN_URLS.salesRevenue.productSales}
            />
          </>
        }
        content={
          <div>
            <div id="override-edit-selection-bar" className="edit-selections-bar">
              <div className="top-filter">
                <div>
                  {isRevenueCategoryView && useSecondaryCategories && (
                    <div className="select-box-no-width">
                      <p>Primary Category</p>
                      {!primaryCategoriesLoading ? (
                        <PrimaryCategory />
                      ) : (
                        <LoadingAnimation uiLoader />
                      )}
                    </div>
                  )}
                  {isRevenueCategoryView && (
                    <div className="select-box-no-width">
                      <p>{`${useSecondaryCategories ? "Secondary" : ""} Category`}</p>
                      {!secondaryCategoriesLoading ? (
                        <SecondaryCategory />
                      ) : (
                        <LoadingAnimation uiLoader />
                      )}
                    </div>
                  )}
                  {isRevenueCategoryView && useSecondaryCategories && (
                    <div className="select-box-no-width">
                      <p>Group By Secondary</p>
                      <GroupCategory />
                    </div>
                  )}
                </div>
                <div className="top-right-filter">
                  <div className="select-box-no-width">
                    <p>Period Type</p>
                    <PeriodType />
                  </div>
                  <div className="select-box-no-width date-range-select-box">
                    <p>Date Range</p>
                    <CustomDateRanges />
                  </div>

                  <div className="date-select-box select-box multi">
                    <p>Date</p>
                    <DatePicker />
                  </div>

                  {renderUpdateBtn()}
                  {renderResetBtn()}
                </div>
              </div>
              <div className="bottom-filter">
                <div>
                  {includeReturnsLabel && (
                    <div className="select-box-no-width">
                      <p>{includeReturnsLabel}</p>
                      <IncludeReturns label={includeReturnsLabel} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="chart-root">
              {updatesLoading ? (
                <LoadingAnimation text="Updating data..." />
              ) : (
                <TableOrStackedBarChart />
              )}
            </div>
          </div>
        }
      />
    </div>
  );
};

export default memo(ProductSalesModule);
